import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'

const DEMO_ITEMS = [
  { emoji: '🥛', name: 'Milk',    tone: 'critical',   label: 'Need now' },
  { emoji: '🥚', name: 'Eggs',    tone: 'critical',   label: 'Need now' },
  { emoji: '🍞', name: 'Bread',   tone: 'terracotta', label: 'Buy soon' },
  { emoji: '🧀', name: 'Cheese',  tone: 'forest',     label: 'Good'     },
  { emoji: '🍌', name: 'Bananas', tone: 'forest',     label: 'Good'     },
] as const

const TONE_CLASS: Record<'critical' | 'terracotta' | 'forest', { dot: string; text: string }> = {
  critical:   { dot: 'bg-critical',   text: 'text-critical'   },
  terracotta: { dot: 'bg-terracotta', text: 'text-terracotta' },
  forest:     { dot: 'bg-forest',     text: 'text-forest'     },
}

const STEPS = [
  {
    number: '1',
    title: 'Tell us what you have',
    desc: 'Pick the items you usually keep at home. Takes 60 seconds.',
  },
  {
    number: '2',
    title: 'Tap after every shop',
    desc: 'One tap when you get home. Pantry learns what you bought.',
  },
  {
    number: '3',
    title: 'Never run out again',
    desc: "Pantry tells you what you need before you notice it's gone.",
  },
]

const PROOF = [
  { number: '$1,500', label: 'wasted per year by the average household' },
  { number: '90 sec', label: 'to unpack after every shop' },
  { number: 'Free',   label: 'to start — no card needed' },
]

export default function LandingPage() {
  const navigate = useNavigate()

  useEffect(() => { document.title = 'Pantry — Never run out of anything again' }, [])

  return (
    <div className="min-h-[100dvh] bg-cream" style={{ overflowX: 'hidden' }}>

      {/* ── Nav ────────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center h-7 w-7 rounded-[9px] bg-forest text-forest-ink font-serif italic text-[16px] font-semibold leading-none">
            P
          </div>
          <span className="font-serif text-[15px] font-medium text-ink italic">Pantry</span>
        </div>
        <Link to="/sign-in" className="text-[13px] font-semibold text-forest underline underline-offset-4 decoration-forest/30">
          Sign in
        </Link>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="min-h-[100dvh] flex flex-col items-center justify-center px-6 pt-24 pb-8 text-center">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center"
        >
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-[16px] bg-forest text-forest-ink font-serif italic text-[32px] font-semibold leading-none mb-6">
            P
          </div>

          <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-3">
            Free to start
          </div>

          <h1 className="font-serif text-[36px] sm:text-[48px] font-normal leading-[1.05] tracking-[-0.02em] text-ink text-center max-w-[520px] mx-auto mb-5">
            Your household's <em className="italic">pantry</em>, finally.
          </h1>

          <p className="font-serif text-[15px] sm:text-[17px] italic text-ink-muted text-center max-w-[440px] mx-auto mb-8 leading-[1.5]">
            Know what you have, what you need, and what's about to expire — on every device, for everyone in your home.
          </p>

          <button
            onClick={() => navigate('/sign-up')}
            className="rounded-[14px] bg-forest text-forest-ink px-8 py-3.5 text-[15px] font-bold tracking-[-0.01em] transition"
          >
            Get started free
          </button>

          <p className="font-serif text-[12px] italic text-ink-muted mt-4">
            No credit card required.
          </p>

          <Link
            to="/install-ios"
            className="font-serif text-[12px] italic text-ink-muted mt-2 underline-offset-4 underline decoration-ink-muted/30 hover:text-ink"
          >
            On iPhone? See install instructions →
          </Link>
        </motion.div>

        {/* Phone mockup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative mx-auto mt-12 max-w-[280px] w-full"
        >
          <div className="relative bg-ink rounded-[40px] p-3 shadow-2xl">
            <div className="bg-cream rounded-[32px] overflow-hidden aspect-[9/19]">

              {/* Status bar */}
              <div className="bg-cream px-6 py-3 flex justify-between items-center">
                <span className="text-[11px] font-medium text-ink">9:41</span>
                <span className="text-[11px] text-ink">●●●</span>
              </div>

              {/* App header */}
              <div className="px-5 pb-3 text-left">
                <div className="text-[9px] uppercase tracking-[0.16em] font-medium text-ink-muted">
                  Your pantry
                </div>
                <p className="font-serif text-[17px] font-medium text-ink leading-tight mt-0.5">
                  3 need <em className="italic">attention</em>
                </p>
              </div>

              {/* Summary bar */}
              <div className="mx-5 mb-4 flex gap-2">
                <div className="flex-1 bg-critical-bg rounded-[10px] px-3 py-2 text-center">
                  <p className="font-serif text-[14px] font-medium text-critical">2</p>
                  <p className="text-[9px] italic font-serif text-critical">out</p>
                </div>
                <div className="flex-1 bg-warn-bg rounded-[10px] px-3 py-2 text-center">
                  <p className="font-serif text-[14px] font-medium text-terracotta">1</p>
                  <p className="text-[9px] italic font-serif text-terracotta">low</p>
                </div>
                <div className="flex-1 bg-ok-bg rounded-[10px] px-3 py-2 text-center">
                  <p className="font-serif text-[14px] font-medium text-forest">8</p>
                  <p className="text-[9px] italic font-serif text-forest">good</p>
                </div>
              </div>

              {/* Item rows */}
              {DEMO_ITEMS.map(item => {
                const cls = TONE_CLASS[item.tone]
                return (
                  <div
                    key={item.name}
                    className="flex items-center gap-3 px-5 py-2.5 border-b border-border-hairline"
                  >
                    <div className={`h-2 w-2 rounded-full shrink-0 ${cls.dot}`} />
                    <span className="text-base">{item.emoji}</span>
                    <span className="font-serif text-[11px] font-medium text-ink flex-1 text-left">{item.name}</span>
                    <span className={`text-[9px] italic font-serif ${cls.text}`}>{item.label}</span>
                  </div>
                )
              })}

              {/* I just shopped button */}
              <div className="mx-5 mt-4">
                <div className="w-full py-3 rounded-[10px] bg-forest text-forest-ink text-[11px] font-bold tracking-[-0.01em] text-center">
                  I just shopped
                </div>
              </div>

            </div>
          </div>

          {/* Glow */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-48 h-12 bg-forest/20 blur-2xl rounded-full" />
        </motion.div>

      </section>

      {/* ── Proof points ───────────────────────────────────────────────────── */}
      <motion.div
        className="flex gap-8 justify-center mt-8 mb-16 px-6 flex-wrap"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        {PROOF.map(({ number, label }) => (
          <div key={number} className="text-center max-w-[120px]">
            <p className="font-serif text-[26px] font-medium text-ink tabular-nums">{number}</p>
            <p className="font-serif text-[11px] italic text-ink-muted mt-1 leading-[1.4]">{label}</p>
          </div>
        ))}
      </motion.div>

      {/* ── How it works ───────────────────────────────────────────────────── */}
      <div className="max-w-[520px] mx-auto px-6 mb-16">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted text-center mb-2">
          How it works
        </div>
        <h2 className="font-serif text-[24px] font-normal text-ink text-center tracking-[-0.015em] mb-8">
          Three simple <em className="italic">steps</em>.
        </h2>

        <div className="space-y-6">
          {STEPS.map(({ number, title, desc }) => (
            <motion.div
              key={number}
              className="flex gap-4 items-start"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <div className="h-8 w-8 rounded-full bg-forest text-forest-ink flex items-center justify-center font-serif text-[14px] font-medium shrink-0 mt-0.5">
                {number}
              </div>
              <div>
                <p className="font-serif text-[15px] font-medium text-ink tracking-[-0.01em]">{title}</p>
                <p className="font-serif text-[13px] italic text-ink-muted mt-0.5 leading-[1.45]">{desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Testimonial ────────────────────────────────────────────────────── */}
      <div className="max-w-[520px] mx-auto px-6 mb-16">
        <div className="rounded-[18px] border border-border-sand bg-paper p-6">
          <p className="font-serif text-[15px] italic text-ink leading-[1.55] mb-4">
            "I used to run out of the same things every single week. Now Pantry tells me before I even notice. My grocery trips take half the time."
          </p>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-forest text-forest-ink flex items-center justify-center font-serif text-[14px] font-medium">
              S
            </div>
            <div>
              <p className="font-serif text-[13px] font-medium text-ink">Sarah M.</p>
              <p className="font-serif text-[11px] italic text-ink-muted">Family of 4 in Chicago</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom CTA ─────────────────────────────────────────────────────── */}
      <div className="text-center pb-24 px-6">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
          Ready
        </div>
        <h2 className="font-serif text-[28px] font-normal text-ink tracking-[-0.015em] mb-3">
          Stop running <em className="italic">out</em>.
        </h2>
        <p className="font-serif text-[14px] italic text-ink-muted mb-6">
          Free forever. Pantry+ when you want more.
        </p>
        <button
          onClick={() => navigate('/sign-up')}
          className="rounded-[14px] bg-forest text-forest-ink px-8 py-3.5 text-[15px] font-bold tracking-[-0.01em] transition"
        >
          Get started free
        </button>
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="border-t border-border-sand py-6 px-6">
        <div className="max-w-[520px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center h-6 w-6 rounded-[7px] bg-forest text-forest-ink font-serif italic text-[13px] font-semibold leading-none">
              P
            </div>
            <span className="font-serif text-[13px] italic text-ink-muted">Pantry</span>
          </div>
          <div className="flex gap-4">
            <a href="/privacy"                         className="font-serif text-[11px] italic text-ink-muted hover:text-ink">Privacy</a>
            <a href="/terms"                           className="font-serif text-[11px] italic text-ink-muted hover:text-ink">Terms</a>
            <a href="mailto:privacy@pantryioapp.com"   className="font-serif text-[11px] italic text-ink-muted hover:text-ink">Contact</a>
          </div>
        </div>
      </footer>

    </div>
  )
}
